// Get the canonical colourways available for a POD product
export const getAvailableProductColourways = (canonicalProduct) => {

    // If the canonical product is missing, return an empty list
    if (!canonicalProduct) return [];

    // Support the direct canonical product shape used in the POD product UI
    if (Array.isArray(canonicalProduct?.custom_product?.colourways)) {

        // Return the available colourways from the canonical product custom product
        return canonicalProduct.custom_product.colourways;

    }

    // Support the selected design/template product shape where the canonical product is nested
    if (Array.isArray(canonicalProduct?.pod_product?.custom_product?.colourways)) {

        // Return the available colourways from the nested canonical product custom product
        return canonicalProduct.pod_product.custom_product.colourways;

    }

    // Return an empty list if no available colourways are present
    return [];

};



// Convert colourway objects or strings into colourway codes
export const normaliseColourwayCodes = (colourways) => {

    // If there are no colourways, return an empty list
    if (!Array.isArray(colourways)) return [];

    // Convert each colourway into a code string
    return colourways
        .map((colourway) => {

            // If the colourway is already a string, use it directly
            if (typeof colourway === "string") return colourway;

            // If the colourway is an object, use its code field
            return colourway?.code || "";

        })
        .filter((colourwayCode) => {

            // Remove any blank codes
            return String(colourwayCode || "").trim() !== "";

        })
        .map((colourwayCode) => {

            // Trim the code for consistent comparisons and payloads
            return String(colourwayCode).trim();

        });

};



// Check whether a design/template product is deriving colourways from the canonical POD product
export const productUsesAllColourways = (product) => {

    // Missing product data should be treated as using all colourways for initial display
    if (!product) return true;

    // Null means use all canonical POD product colourways
    if (product.colourways === null) return true;

    // Missing colourways means no explicit selection has been stored, so derive from the canonical POD product
    if (typeof product.colourways === "undefined") return true;

    // An empty array from the API means there are no explicit selected colourway rows stored
    if (Array.isArray(product.colourways) && product.colourways.length === 0) return true;

    // Anything else is an explicit colourway selection
    return false;

};



// Get the available colourway codes for a canonical POD product
export const getAvailableProductColourwayCodes = (canonicalProduct) => {

    // Get the available colourways from the canonical product
    const availableColourways = getAvailableProductColourways(canonicalProduct);

    // Return the available colourway codes
    return normaliseColourwayCodes(availableColourways);

};



// Get the currently selected colourway codes for a design/template product
export const getSelectedProductColourwayCodes = (product) => {

    // Null means use all canonical colourways, so there is no explicit selected list
    if (productUsesAllColourways(product)) return [];

    // Return the selected colourway codes from the design/template product
    return normaliseColourwayCodes(product?.colourways);

};


// Get the effective selected colourway codes for a configured product
export const getEffectiveProductColourwayCodes = (product, canonicalProduct) => {

    // Products using all colourways inherit the complete canonical colourway list
    if (productUsesAllColourways(product)) return getAvailableProductColourwayCodes(canonicalProduct);

    // Otherwise use the configured colourway selection
    return getSelectedProductColourwayCodes(product);

};


// Get the default colourway code for a design/template product
export const getDefaultProductColourwayCode = (product) => {

    // Return an empty string if no default colourway is set
    if (!product?.default_colourway) return "";

    // Return the trimmed default colourway code
    return String(product.default_colourway).trim();

};



// Build the default colourway selection for a new design/template product
export const buildDefaultColourwaySelection = (canonicalProduct, {
    defaultColourwayCode = "",
    defaultColourwayInherited = "N"
} = {}) => {

    // Get the available colourway codes from the canonical product
    const availableColourwayCodes = getAvailableProductColourwayCodes(canonicalProduct);

    // If the canonical product has no available colourways, there is nothing to add to the product payload
    if (availableColourwayCodes.length === 0) return {};

    // Make sure the requested default exists on this canonical product
    const normalisedDefaultColourway = normaliseDefaultColourway(availableColourwayCodes, defaultColourwayCode);

    // Derive colourways from the canonical POD product by default
    return {

        // Null tells the API to use all canonical POD product colourways
        colourways: null,

        // Keep the effective default available to the portal and every API consumer
        default_colourway: normalisedDefaultColourway,

        // Tell the API whether this value is inherited from the configured parent
        default_colourway_inherited: String(defaultColourwayInherited || "N").trim().toUpperCase() === "Y" ? "Y" : "N"

    };

};



// Remove unavailable colourways from a selected colourway code list
export const filterSelectedColourwaysToAvailable = (selectedColourwayCodes, availableColourways) => {

    // Convert available colourways into code strings
    const availableColourwayCodes = normaliseColourwayCodes(availableColourways);

    // Convert selected colourways into code strings
    const safeSelectedColourwayCodes = normaliseColourwayCodes(selectedColourwayCodes);

    // Keep only selected colourways that still exist in the available colourway list
    return safeSelectedColourwayCodes.filter((colourwayCode) => {

        // Check this colourway code is available for the canonical POD product
        return availableColourwayCodes.includes(colourwayCode);

    });

};



// Ensure the default colourway is valid for the selected colourways
export const normaliseDefaultColourway = (selectedColourwayCodes, defaultColourwayCode) => {

    // Convert selected colourways into code strings
    const safeSelectedColourwayCodes = normaliseColourwayCodes(selectedColourwayCodes);

    // Trim the default colourway code
    const safeDefaultColourwayCode = String(defaultColourwayCode || "").trim();

    // If the current default is included in the selected colourways, keep it
    if (safeDefaultColourwayCode && safeSelectedColourwayCodes.includes(safeDefaultColourwayCode)) return safeDefaultColourwayCode;

    // If there is no valid default, use the first selected colourway
    if (safeSelectedColourwayCodes.length > 0) return safeSelectedColourwayCodes[0];

    // Return an empty string if no selected colourways exist
    return "";

};



// Build the colourway payload fields for a design/template product
export const buildProductColourwayPayload = (selectedColourwayCodes, defaultColourwayCode, availableColourways, defaultColourwayInherited = "N") => {

    // Convert available colourways into code strings
    const availableColourwayCodes = normaliseColourwayCodes(availableColourways);

    // If the canonical POD product has no available colourways, omit colourway fields entirely
    if (availableColourwayCodes.length === 0) return {};

    // If selected colourways is null, tell the API to derive all colourways from the canonical POD product
    if (selectedColourwayCodes === null) {

        // Work out the valid default colourway from the available colourways
        const normalisedDefaultColourway = normaliseDefaultColourway(availableColourwayCodes, defaultColourwayCode);

        // Return null colourways with a default code
        return {

            // Null means use all canonical POD product colourways
            colourways: null,

            // Send the effective default colourway code
            default_colourway: normalisedDefaultColourway,

            // Tell the API whether this child inherits its configured parent default
            default_colourway_inherited: String(defaultColourwayInherited || "N").trim().toUpperCase() === "Y" ? "Y" : "N"

        };

    }

    // Keep only selected colourways that exist in the available colourway list
    const filteredSelectedColourwayCodes = filterSelectedColourwaysToAvailable(selectedColourwayCodes, availableColourwayCodes);

    // Work out the valid default colourway from the filtered selected colourways
    const normalisedDefaultColourway = normaliseDefaultColourway(filteredSelectedColourwayCodes, defaultColourwayCode);

    // Return the payload fields expected by the API
    return {

        // Send colourway codes only
        colourways: filteredSelectedColourwayCodes,

        // Send the effective default colourway code
        default_colourway: normalisedDefaultColourway,

        // Tell the API whether this child inherits its configured parent default
        default_colourway_inherited: String(defaultColourwayInherited || "N").trim().toUpperCase() === "Y" ? "Y" : "N"

    };

};



// Check whether a canonical POD product has colourways that need selecting
export const productHasAvailableColourways = (canonicalProduct) => {

    // Return true when at least one available colourway exists
    return getAvailableProductColourways(canonicalProduct).length > 0;

};